import {
    ImageEmbeddingSearchHint,
    LookitItemCategoryId,
    ProductCluster,
    ProductSubcategory,
    TextSignalCandidate
} from './domain';

export type NaverShoppingQueryStatus = 'ready' | 'needs_user_refinement' | 'mock_fallback';
export type QueryBuilderTermKind =
    | 'user_text'
    | 'brand'
    | 'wholesale_tag'
    | 'product_name'
    | 'category'
    | 'color'
    | 'material'
    | 'pattern'
    | 'fit'
    | 'ocr';

export type QueryBuilderRemovedReason =
    | 'empty'
    | 'sns_noise'
    | 'price_noise'
    | 'category_conflict'
    | 'duplicate'
    | 'too_generic'
    | 'too_long';

export type QueryBuilderHintInput =
    | string
    | Pick<ImageEmbeddingSearchHint, 'text' | 'normalizedText' | 'confidence' | 'source'>
    | Pick<TextSignalCandidate, 'text' | 'confidence' | 'source'>
    | null
    | undefined;

export type QueryBuilderProductClusterInput = Pick<
    ProductCluster,
    | 'canonical_name'
    | 'brand_name'
    | 'wholesale_tag_name'
    | 'category_root'
    | 'subcategory'
    | 'colors'
    | 'materials'
    | 'pattern'
    | 'fit'
    | 'cluster_type'
    | 'confidence_level'
>;

export interface QueryBuilderTerm {
    text: string;
    kind: QueryBuilderTermKind;
    source: string;
    confidence: number;
    optional: boolean;
}

export interface QueryBuilderRemovedTerm {
    text: string;
    normalizedText: string;
    kind: QueryBuilderTermKind;
    source: string;
    reason: QueryBuilderRemovedReason;
}

export interface QueryBuilderSectionPromotionGuard {
    brandExactAllowed: boolean;
    wholesaleSameAllowed: boolean;
    reason: 'has_visual_or_user_evidence' | 'hint_only' | 'low_quality_query';
}

export interface NaverShoppingQueryBuilderInput {
    selectedCategoryId?: LookitItemCategoryId | string | null;
    subcategoryHint?: ProductSubcategory | string | null;
    brandHints?: QueryBuilderHintInput[];
    ocrHints?: QueryBuilderHintInput[];
    wholesaleTagHint?: QueryBuilderHintInput;
    wholesaleTagHints?: QueryBuilderHintInput[];
    colorHints?: QueryBuilderHintInput[];
    materialHints?: QueryBuilderHintInput[];
    patternHints?: QueryBuilderHintInput[];
    fitHints?: QueryBuilderHintInput[];
    productNameHints?: QueryBuilderHintInput[];
    userSearchText?: string | null;
    userTextHints?: QueryBuilderHintInput[];
    productClusters?: QueryBuilderProductClusterInput[];
    hasVisualEvidence?: boolean | null;
    maxTerms?: number;
    maxQueryLength?: number;
}

export interface NaverShoppingQueryBuilderResult {
    query: string | null;
    status: NaverShoppingQueryStatus;
    shouldCallProvider: boolean;
    confidence: number;
    terms: QueryBuilderTerm[];
    removedTerms: QueryBuilderRemovedTerm[];
    guardReasons: string[];
    categoryConflict: boolean;
    sectionPromotionGuard: QueryBuilderSectionPromotionGuard;
}

interface HintValue {
    text: string;
    source: string;
    confidence: number | null;
}

interface CategoryContext {
    selectedCategoryId: string | null;
    rootId: string | null;
    rootLabel: string | null;
    subcategory: string | null;
    subcategoryConfirmed: boolean;
}

const defaultMaxTerms = 7;
const defaultMaxQueryLength = 60;

const genericTerms = new Set([
    '옷',
    '의류',
    '패션',
    '상품',
    '쇼핑',
    '스타일',
    '코디',
    '룩',
    '여성의류',
    '남성의류',
    '상의',
    '하의',
    '아우터',
    '원피스',
    '신발',
    '가방',
    '액세서리',
    '블라우스',
    '티셔츠',
    '셔츠',
    '니트',
    '가디건',
    '팬츠',
    '바지',
    '데님',
    '스커트',
    '쇼츠',
    '반바지',
    '슬랙스',
    'dress',
    'fashion',
    'clothes',
    'clothing',
    'top',
    'bottom',
    'pants',
    'skirt',
    'shirt',
    'blouse',
    'bag',
    'shoes'
]);

const snsNoisePatterns = [
    /(?:instagram|insta|tiktok|틱톡|youtube|유튜브|shorts?|reels?|regram|follow|팔로우|좋아요|댓글|공유|dm|direct message)/i,
    /(?:ootd|dailylook|daily look|데일리룩|착장)/i
];

const rootConflictPatterns: Record<string, RegExp> = {
    top: /상의|블라우스|티셔츠|셔츠|니트|가디건|\btop\b|blouse|t-?shirt|\bshirt\b|knit|cardigan/i,
    bottom: /하의|팬츠|바지|데님|청바지|슬랙스|스커트|쇼츠|반바지|레깅스|\bbottom\b|pants?|trousers?|denim|jeans?|slacks?|skirt|shorts?|leggings?/i,
    outer: /아우터|코트|자켓|재킷|점퍼|패딩|블루종|outer|coat|jacket|jumper|padding|blouson/i,
    dress: /원피스|드레스|dress/i,
    shoes: /신발|슈즈|운동화|로퍼|부츠|shoes?|sneakers?|loafer|boots?/i,
    bag: /가방|백|토트|숄더백|크로스백|bag|tote|pouch/i,
    accessory: /액세서리|악세서리|주얼리|목걸이|귀걸이|반지|accessory|jewelry|necklace|earrings?|ring/i
};

const bottomPantsPattern = /팬츠|바지|데님|청바지|슬랙스|pants?|trousers?|denim|jeans?|slacks?/i;
const bottomSkirtPattern = /스커트|skirt/i;
const bottomShortsPattern = /쇼츠|반바지|shorts?/i;
const bottomLeggingsPattern = /레깅스|leggings?/i;

const categoryLabelByRootId: Record<string, string> = {
    top: '상의',
    bottom: '하의',
    outer: '아우터',
    dress: '원피스',
    shoes: '신발',
    bag: '가방',
    accessory: '액세서리'
};

const subcategoryAliases: Record<string, string> = {
    pants: '팬츠',
    pant: '팬츠',
    trouser: '팬츠',
    trousers: '팬츠',
    slacks: '팬츠',
    '슬랙스': '팬츠',
    '바지': '팬츠',
    '팬츠': '팬츠',
    denim: '데님',
    jeans: '데님',
    jean: '데님',
    '청바지': '데님',
    '데님': '데님',
    skirt: '스커트',
    '스커트': '스커트',
    shorts: '쇼츠',
    short: '쇼츠',
    '쇼츠': '쇼츠',
    '반바지': '쇼츠',
    leggings: '레깅스',
    legging: '레깅스',
    '레깅스': '레깅스',
    blouse: '블라우스',
    '블라우스': '블라우스',
    tshirt: '티셔츠',
    't-shirt': '티셔츠',
    tee: '티셔츠',
    '티셔츠': '티셔츠',
    knit: '니트',
    '니트': '니트',
    shirt: '셔츠',
    '셔츠': '셔츠',
    cardigan: '가디건',
    '가디건': '가디건'
};

const attributeAliases: Record<string, string> = {
    cotton: '코튼',
    polyester: '폴리에스터',
    rayon: '레이온',
    nylon: '나일론',
    wool: '울',
    acrylic: '아크릴',
    polyurethane: '스판',
    denim: '데님',
    solid: '무지',
    lace: '레이스',
    rib: '골지',
    cable: '케이블',
    semi_crop: '세미 크롭',
    'semi-crop': '세미 크롭',
    relaxed: '루즈핏',
    straight: '스트레이트',
    standard: '스탠다드',
    regular: '레귤러',
    jogger: '조거',
    wide: '와이드'
};

function clampScore(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(1, Math.round(value * 1000) / 1000));
}

function normalizeKey(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function compactKey(value: string): string {
    return normalizeKey(value).replace(/\s+/g, '');
}

function categoryBaseId(categoryId?: string | null): string | null {
    const [baseId] = (categoryId || '').split(':');
    return baseId || null;
}

function normalizeSubcategory(value?: string | null): string | null {
    const normalized = normalizeKey(value || '');
    if (!normalized) return null;
    return subcategoryAliases[normalized] || value?.trim() || null;
}

function subcategoryFromCategoryId(categoryId?: string | null): string | null {
    const parts = (categoryId || '').split(':');
    return normalizeSubcategory(parts.length > 1 ? parts.slice(1).join(':') : null);
}

function categoryContext(input: NaverShoppingQueryBuilderInput): CategoryContext {
    const selectedCategoryId = input.selectedCategoryId?.trim() || null;
    const rootId = categoryBaseId(selectedCategoryId);
    const subcategory = normalizeSubcategory(input.subcategoryHint || subcategoryFromCategoryId(selectedCategoryId));
    return {
        selectedCategoryId,
        rootId,
        rootLabel: rootId ? categoryLabelByRootId[rootId] || null : null,
        subcategory,
        subcategoryConfirmed: Boolean(input.subcategoryHint || subcategoryFromCategoryId(selectedCategoryId))
    };
}

function hintValue(input: QueryBuilderHintInput, fallbackSource: string): HintValue | null {
    if (typeof input === 'string') {
        return { text: input, source: fallbackSource, confidence: null };
    }
    if (!input || typeof input !== 'object') return null;
    const text = 'normalizedText' in input && input.normalizedText ? input.normalizedText : input.text;
    if (typeof text !== 'string') return null;
    const source = 'source' in input && typeof input.source === 'string' ? input.source : fallbackSource;
    const confidence = typeof input.confidence === 'number' ? input.confidence : null;
    return { text, source, confidence };
}

function pricePatternHit(text: string): boolean {
    return /[₩￦]?\s*\d{1,3}(?:,\d{3})+(?:\s*원|won|krw)?/i.test(text)
        || /\d+\s*(?:원|won|krw)/i.test(text)
        || /\d+\s*%/.test(text);
}

function snsPatternHit(text: string): boolean {
    return /(?:^|\s)@[\w._-]{2,}/.test(text)
        || /https?:\/\/\S+|www\.\S+/i.test(text)
        || snsNoisePatterns.some((pattern) => pattern.test(text));
}

export function sanitizeQueryText(value: string): string {
    return value
        .replace(/https?:\/\/\S+|www\.\S+/gi, ' ')
        .replace(/(?:^|\s)@[\w._-]{2,}/g, ' ')
        .replace(/#[\w가-힣._-]+/g, ' ')
        .replace(/[₩￦]?\s*\d{1,3}(?:,\d{3})+(?:\s*원|won|krw)?/gi, ' ')
        .replace(/\d+\s*(?:원|won|krw)/gi, ' ')
        .replace(/\d+\s*%/g, ' ')
        .replace(/(?:instagram|insta|tiktok|틱톡|youtube|유튜브|shorts?|reels?|regram|follow|팔로우|좋아요|댓글|공유|dm|direct message)/gi, ' ')
        .replace(/(?:ootd|dailylook|daily look|데일리룩|착장)/gi, ' ')
        .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, ' ')
        .replace(/[^\p{L}\p{N}\s._-]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeAttributeText(value: string): string {
    const normalized = normalizeKey(value);
    return attributeAliases[normalized] || value.trim();
}

function hasCategoryConflict(text: string, context: CategoryContext): boolean {
    const selectedRootId = context.rootId;
    if (!selectedRootId || !(selectedRootId in rootConflictPatterns)) return false;

    for (const [rootId, pattern] of Object.entries(rootConflictPatterns)) {
        if (rootId !== selectedRootId && pattern.test(text)) return true;
    }

    if (selectedRootId !== 'bottom' || !context.subcategory) return false;
    const subcategory = normalizeSubcategory(context.subcategory);
    if (subcategory === '팬츠' || subcategory === '데님') {
        return bottomSkirtPattern.test(text) || bottomShortsPattern.test(text) || bottomLeggingsPattern.test(text);
    }
    if (subcategory === '스커트') return bottomPantsPattern.test(text) || bottomShortsPattern.test(text) || bottomLeggingsPattern.test(text);
    if (subcategory === '쇼츠') return bottomPantsPattern.test(text) || bottomSkirtPattern.test(text) || bottomLeggingsPattern.test(text);
    if (subcategory === '레깅스') return bottomPantsPattern.test(text) || bottomSkirtPattern.test(text) || bottomShortsPattern.test(text);
    return false;
}

function isGenericPhrase(text: string): boolean {
    const cleaned = sanitizeQueryText(text);
    if (!cleaned) return true;
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    return tokens.every((token) => genericTerms.has(compactKey(token)));
}

function defaultConfidence(kind: QueryBuilderTermKind, hintConfidence: number | null, optional: boolean): number {
    const baseByKind: Record<QueryBuilderTermKind, number> = {
        user_text: 0.94,
        brand: 0.72,
        wholesale_tag: 0.74,
        product_name: 0.78,
        category: optional ? 0.45 : 0.86,
        color: 0.62,
        material: 0.58,
        pattern: 0.56,
        fit: 0.54,
        ocr: 0.48
    };
    const capByKind: Record<QueryBuilderTermKind, number> = {
        user_text: 0.98,
        brand: 0.82,
        wholesale_tag: 0.8,
        product_name: 0.84,
        category: optional ? 0.48 : 0.9,
        color: 0.74,
        material: 0.7,
        pattern: 0.68,
        fit: 0.66,
        ocr: 0.62
    };
    const raw = typeof hintConfidence === 'number' ? hintConfidence : baseByKind[kind];
    return clampScore(Math.min(raw, capByKind[kind]));
}

function termSortPriority(kind: QueryBuilderTermKind): number {
    const priority: Record<QueryBuilderTermKind, number> = {
        user_text: 5,
        brand: 10,
        wholesale_tag: 12,
        product_name: 20,
        ocr: 24,
        category: 30,
        color: 40,
        material: 50,
        pattern: 60,
        fit: 70
    };
    return priority[kind];
}

function appendRemoved(
    removedTerms: QueryBuilderRemovedTerm[],
    input: {
        text: string;
        kind: QueryBuilderTermKind;
        source: string;
        reason: QueryBuilderRemovedReason;
    }
) {
    removedTerms.push({
        text: input.text,
        normalizedText: normalizeKey(input.text),
        kind: input.kind,
        source: input.source,
        reason: input.reason
    });
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function removeExistingBrandPrefix(text: string, terms: QueryBuilderTerm[]): string {
    let result = text;
    terms
        .filter((term) => term.kind === 'brand' || term.kind === 'wholesale_tag')
        .forEach((term) => {
            const pattern = new RegExp(`(^|\\s)${escapeRegExp(term.text)}(?=\\s|$)`, 'i');
            result = result.replace(pattern, ' ');
        });
    return result.replace(/\s+/g, ' ').trim();
}

function appendTerm(
    terms: QueryBuilderTerm[],
    removedTerms: QueryBuilderRemovedTerm[],
    seen: Set<string>,
    context: CategoryContext,
    input: {
        rawText: string;
        kind: QueryBuilderTermKind;
        source: string;
        confidence: number | null;
        optional?: boolean;
    }
) {
    const hadSnsNoise = snsPatternHit(input.rawText);
    const hadPriceNoise = pricePatternHit(input.rawText);
    const normalizedRawText = input.kind === 'material' || input.kind === 'pattern' || input.kind === 'fit'
        ? normalizeAttributeText(input.rawText)
        : input.rawText;
    let text = sanitizeQueryText(normalizedRawText);
    if (!text) {
        appendRemoved(removedTerms, {
            text: input.rawText,
            kind: input.kind,
            source: input.source,
            reason: hadPriceNoise ? 'price_noise' : hadSnsNoise ? 'sns_noise' : 'empty'
        });
        return;
    }
    if (input.kind === 'ocr' || input.kind === 'product_name' || input.kind === 'user_text') {
        const deDuplicatedText = removeExistingBrandPrefix(text, terms);
        if (deDuplicatedText) text = deDuplicatedText;
    }
    if (hadSnsNoise) {
        appendRemoved(removedTerms, {
            text: input.rawText,
            kind: input.kind,
            source: input.source,
            reason: 'sns_noise'
        });
    }
    if (hadPriceNoise) {
        appendRemoved(removedTerms, {
            text: input.rawText,
            kind: input.kind,
            source: input.source,
            reason: 'price_noise'
        });
    }

    if (hasCategoryConflict(text, context)) {
        appendRemoved(removedTerms, {
            text,
            kind: input.kind,
            source: input.source,
            reason: 'category_conflict'
        });
        return;
    }

    const key = compactKey(text);
    if (seen.has(key)) {
        appendRemoved(removedTerms, {
            text,
            kind: input.kind,
            source: input.source,
            reason: 'duplicate'
        });
        return;
    }

    if (!['user_text', 'product_name', 'category'].includes(input.kind) && isGenericPhrase(text)) {
        appendRemoved(removedTerms, {
            text,
            kind: input.kind,
            source: input.source,
            reason: 'too_generic'
        });
        return;
    }

    if (
        ['category', 'color', 'material', 'pattern', 'fit'].includes(input.kind)
        && terms.some((term) => compactKey(term.text).includes(key))
    ) {
        appendRemoved(removedTerms, {
            text,
            kind: input.kind,
            source: input.source,
            reason: 'duplicate'
        });
        return;
    }

    seen.add(key);
    terms.push({
        text,
        kind: input.kind,
        source: input.source,
        confidence: defaultConfidence(input.kind, input.confidence, input.optional || false),
        optional: input.optional || false
    });
}

function appendHints(
    terms: QueryBuilderTerm[],
    removedTerms: QueryBuilderRemovedTerm[],
    seen: Set<string>,
    context: CategoryContext,
    kind: QueryBuilderTermKind,
    source: string,
    hints?: QueryBuilderHintInput[],
    optional = false
) {
    (hints || []).forEach((hint) => {
        const value = hintValue(hint, source);
        if (!value) return;
        appendTerm(terms, removedTerms, seen, context, {
            rawText: value.text,
            kind,
            source: value.source || source,
            confidence: value.confidence,
            optional
        });
    });
}

function clusterHints(input: NaverShoppingQueryBuilderInput): Required<Pick<
    NaverShoppingQueryBuilderInput,
    'brandHints' | 'wholesaleTagHints' | 'productNameHints' | 'colorHints' | 'materialHints' | 'patternHints' | 'fitHints'
>> {
    const clusters = input.productClusters || [];
    const primary = clusters[0] || null;
    const secondaryWholesale = clusters.find((cluster) => cluster.wholesale_tag_name)?.wholesale_tag_name || null;
    return {
        brandHints: primary?.brand_name ? [primary.brand_name] : [],
        wholesaleTagHints: primary?.wholesale_tag_name
            ? [primary.wholesale_tag_name]
            : secondaryWholesale
                ? [secondaryWholesale]
                : [],
        productNameHints: primary?.canonical_name ? [primary.canonical_name] : [],
        colorHints: primary?.colors?.slice(0, 1) || [],
        materialHints: primary?.materials?.slice(0, 1) || [],
        patternHints: primary?.pattern ? [primary.pattern] : [],
        fitHints: primary?.fit ? [primary.fit] : []
    };
}

function categoryTerms(context: CategoryContext): Array<{ text: string; optional: boolean }> {
    if (!context.rootId) return [];
    if (context.subcategory) return [{ text: context.subcategory, optional: !context.subcategoryConfirmed }];
    if (context.rootId === 'bottom') return [{ text: '팬츠', optional: true }];
    if (context.rootId === 'top') return [{ text: '상의', optional: true }];
    return context.rootLabel ? [{ text: context.rootLabel, optional: true }] : [];
}

function selectedQueryTerms(
    terms: QueryBuilderTerm[],
    removedTerms: QueryBuilderRemovedTerm[],
    maxTerms: number,
    maxQueryLength: number
): QueryBuilderTerm[] {
    const sorted = [...terms].sort((a, b) => {
        const priorityDiff = termSortPriority(a.kind) - termSortPriority(b.kind);
        if (priorityDiff !== 0) return priorityDiff;
        return b.confidence - a.confidence;
    });
    const selected: QueryBuilderTerm[] = [];

    for (const term of sorted) {
        if (selected.length >= maxTerms) {
            appendRemoved(removedTerms, {
                text: term.text,
                kind: term.kind,
                source: term.source,
                reason: 'too_long'
            });
            continue;
        }
        const candidate = selected.concat(term).map((item) => item.text).join(' ');
        if (candidate.length > maxQueryLength) {
            appendRemoved(removedTerms, {
                text: term.text,
                kind: term.kind,
                source: term.source,
                reason: 'too_long'
            });
            continue;
        }
        selected.push(term);
    }

    return selected;
}

function queryConfidence(terms: QueryBuilderTerm[], categoryConflict: boolean): number {
    if (terms.length === 0) return 0;
    if (isGenericPhrase(terms.map((term) => term.text).join(' '))) return 0.1;
    const total = terms.reduce((sum, term) => sum + term.confidence, 0);
    const average = total / terms.length;
    const specificBonus = terms.some((term) => term.kind === 'product_name' || term.kind === 'user_text') ? 0.08 : 0;
    const conflictPenalty = categoryConflict ? 0.24 : 0;
    return clampScore(average + specificBonus - conflictPenalty);
}

function guardReasonsForTerms(terms: QueryBuilderTerm[], confidence: number, categoryConflict: boolean): string[] {
    const reasons: string[] = [];
    const queryText = terms.map((term) => term.text).join(' ');
    const onlyGeneric = !queryText || isGenericPhrase(queryText);
    const hasSpecificProduct = terms.some((term) => term.kind === 'product_name' || term.kind === 'user_text');
    const hasAttribute = terms.some((term) => ['color', 'material', 'pattern', 'fit'].includes(term.kind));
    const hasCategory = terms.some((term) => term.kind === 'category');
    const hasBrandLike = terms.some((term) => term.kind === 'brand' || term.kind === 'wholesale_tag');

    if (terms.length === 0) reasons.push('empty_query');
    if (onlyGeneric) reasons.push('generic_query');
    if (categoryConflict) reasons.push('category_query_conflict');
    if (confidence < 0.45) reasons.push('low_confidence_query');
    if (hasBrandLike && hasCategory && !hasSpecificProduct && !hasAttribute) reasons.push('brand_category_only');
    if (terms.length === 1 && !hasSpecificProduct) reasons.push('single_weak_term');
    return reasons;
}

function statusForQuery(terms: QueryBuilderTerm[], confidence: number, guardReasons: string[]): NaverShoppingQueryStatus {
    if (guardReasons.includes('empty_query') || guardReasons.includes('generic_query')) return 'needs_user_refinement';
    if (guardReasons.includes('category_query_conflict') && confidence < 0.45) return 'mock_fallback';
    if (guardReasons.includes('brand_category_only') || guardReasons.includes('single_weak_term')) return 'mock_fallback';
    if (confidence < 0.45) return 'mock_fallback';
    if (terms.map((term) => term.text).join(' ').length < 2) return 'needs_user_refinement';
    return 'ready';
}

function promotionGuard(
    status: NaverShoppingQueryStatus,
    terms: QueryBuilderTerm[],
    hasVisualEvidence: boolean
): QueryBuilderSectionPromotionGuard {
    const hasUserOrProductEvidence = terms.some((term) => term.kind === 'user_text' || term.kind === 'product_name');
    if (status !== 'ready') {
        return {
            brandExactAllowed: false,
            wholesaleSameAllowed: false,
            reason: 'low_quality_query'
        };
    }
    if (!hasVisualEvidence && !hasUserOrProductEvidence) {
        return {
            brandExactAllowed: false,
            wholesaleSameAllowed: false,
            reason: 'hint_only'
        };
    }
    return {
        brandExactAllowed: true,
        wholesaleSameAllowed: true,
        reason: 'has_visual_or_user_evidence'
    };
}

export function buildNaverShoppingQuery(input: NaverShoppingQueryBuilderInput): NaverShoppingQueryBuilderResult {
    const context = categoryContext(input);
    const removedTerms: QueryBuilderRemovedTerm[] = [];
    const terms: QueryBuilderTerm[] = [];
    const seen = new Set<string>();
    const clusterDerived = clusterHints(input);
    const maxTerms = input.maxTerms || defaultMaxTerms;
    const maxQueryLength = input.maxQueryLength || defaultMaxQueryLength;

    appendHints(terms, removedTerms, seen, context, 'user_text', 'user_hint', [
        input.userSearchText || null,
        ...(input.userTextHints || [])
    ]);
    appendHints(terms, removedTerms, seen, context, 'brand', 'brand', [
        ...(input.brandHints || []),
        ...clusterDerived.brandHints
    ]);
    appendHints(terms, removedTerms, seen, context, 'wholesale_tag', 'wholesale_tag', [
        input.wholesaleTagHint || null,
        ...(input.wholesaleTagHints || []),
        ...clusterDerived.wholesaleTagHints
    ]);
    appendHints(terms, removedTerms, seen, context, 'product_name', 'metadata', [
        ...(input.productNameHints || []),
        ...clusterDerived.productNameHints
    ]);
    appendHints(terms, removedTerms, seen, context, 'ocr', 'ocr', input.ocrHints || []);
    categoryTerms(context).forEach((categoryTerm) => {
        appendTerm(terms, removedTerms, seen, context, {
            rawText: categoryTerm.text,
            kind: 'category',
            source: categoryTerm.optional ? 'category_auxiliary' : 'category',
            confidence: categoryTerm.optional ? 0.45 : 0.86,
            optional: categoryTerm.optional
        });
    });
    appendHints(terms, removedTerms, seen, context, 'color', 'metadata', [
        ...(input.colorHints || []),
        ...clusterDerived.colorHints
    ], true);
    appendHints(terms, removedTerms, seen, context, 'material', 'metadata', [
        ...(input.materialHints || []),
        ...clusterDerived.materialHints
    ], true);
    appendHints(terms, removedTerms, seen, context, 'pattern', 'metadata', [
        ...(input.patternHints || []),
        ...clusterDerived.patternHints
    ], true);
    appendHints(terms, removedTerms, seen, context, 'fit', 'metadata', [
        ...(input.fitHints || []),
        ...clusterDerived.fitHints
    ], true);

    const selectedTerms = selectedQueryTerms(terms, removedTerms, maxTerms, maxQueryLength);
    const categoryConflict = removedTerms.some((term) => term.reason === 'category_conflict');
    const confidence = queryConfidence(selectedTerms, categoryConflict);
    const guardReasons = guardReasonsForTerms(selectedTerms, confidence, categoryConflict);
    const status = statusForQuery(selectedTerms, confidence, guardReasons);
    const query = selectedTerms.map((term) => term.text).join(' ').trim() || null;

    return {
        query,
        status,
        shouldCallProvider: status === 'ready' && Boolean(query),
        confidence,
        terms: selectedTerms,
        removedTerms,
        guardReasons,
        categoryConflict,
        sectionPromotionGuard: promotionGuard(status, selectedTerms, input.hasVisualEvidence === true)
    };
}
